import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { shallow } from 'zustand/shallow';
import { useUsersStore } from '../../store/useUsersStore';


// rating options
const RATING_OPTIONS = [
  { value: '', label: 'Does not matter' },
  { value: '1', label: 'At least 1 star' },
  { value: '2', label: 'At least 2 stars' },
  { value: '3', label: 'At least 3 stars' },
  { value: '4', label: 'At least 4 stars' },
  { value: '5', label: 'At least 5 stars' },
];


// simple select options
const YES_OPTIONS = [
  { value: '', label: 'Does not matter' },
  { value: 'yes', label: 'Yes, verified only' },
];


const DEPOSIT_OPTIONS = [
  { value: '', label: 'Does not matter' },
  { value: 'yes', label: 'Yes, deposit made' },
];


const ClientFilters = ({ subUserId: propSubUserId }) => {
  const users = useUsersStore((s) => s.users);
  const selectedKey = useUsersStore((s) => s.selectedKey);
  const updateSubUser = useUsersStore((s) => s.updateSubUser);
  const storeError = useUsersStore((s) => s.error);
  useUsersStore(shallow);


  // ============ RESOLVE SUB-USER ID ============
  const resolvedSubUserId = useMemo(() => {
    if (propSubUserId) return String(propSubUserId);
    if (!selectedKey || !Array.isArray(users)) return null;
    const found = users.find((u) => {
      const id = u.sub_user_id || u.document_id || u.id;
      if (String(id) === String(selectedKey)) return true;
      if (u.sub_username && String(u.sub_username) === String(selectedKey)) return true;
      return false;
    });
    return found ? String(found.sub_user_id || found.document_id || found.id) : null;
  }, [propSubUserId, selectedKey, users]);


  const finalSubUserId = useMemo(
    () => String(resolvedSubUserId || propSubUserId || selectedKey || '') || null,
    [resolvedSubUserId, propSubUserId, selectedKey]
  );


  // ============ GET CLIENT FILTERS ============
  const clientFilters = useMemo(() => {
    if (!finalSubUserId || !Array.isArray(users)) return {};
    const u = users.find((x) =>
      String(x.sub_user_id || x.document_id || x.id) === String(finalSubUserId) ||
      (x.sub_username && String(x.sub_username) === String(finalSubUserId))
    );
    return (u && u.client_filters) || {};
  }, [finalSubUserId, users]);


  // ============ INITIAL VALUES ============
  const initialValues = useMemo(
    () => ({
      payment_verified: clientFilters.payment_verified ?? '',
      email_verified: clientFilters.email_verified ?? '',
      deposit_made: clientFilters.deposit_made ?? '',
      min_rating: clientFilters.min_rating ?? '',
      min_projects:
        typeof clientFilters.min_projects === 'number'
          ? clientFilters.min_projects
          : clientFilters.min_projects
          ? Number(clientFilters.min_projects)
          : '',
      blacklisted_clients: Array.isArray(clientFilters.blacklisted_clients)
        ? clientFilters.blacklisted_clients.slice()
        : typeof clientFilters.blacklisted_clients === 'string'
        ? clientFilters.blacklisted_clients
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    }),
    [clientFilters]
  );


  const initialJson = useMemo(() => JSON.stringify(initialValues), [initialValues]);


  // ============ LOCAL STATE ============
  const [paymentVerified, setPaymentVerified] = useState(initialValues.payment_verified);
  const [emailVerified, setEmailVerified] = useState(initialValues.email_verified);
  const [depositMade, setDepositMade] = useState(initialValues.deposit_made);
  const [minRating, setMinRating] = useState(initialValues.min_rating);
  const [minProjects, setMinProjects] = useState(initialValues.min_projects);
  const [blacklistedText, setBlacklistedText] = useState(
    (initialValues.blacklisted_clients || []).join(', ')
  );
  const [blacklisted, setBlacklisted] = useState(initialValues.blacklisted_clients || []);


  const prevInitialRef = useRef(initialJson);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);


  // ============ SYNC FROM STORE ============
  useEffect(() => {
    if (prevInitialRef.current !== initialJson) {
      setPaymentVerified(initialValues.payment_verified);
      setEmailVerified(initialValues.email_verified);
      setDepositMade(initialValues.deposit_made);
      setMinRating(initialValues.min_rating);
      setMinProjects(initialValues.min_projects);
      setBlacklisted(initialValues.blacklisted_clients || []);
      setBlacklistedText((initialValues.blacklisted_clients || []).join(', '));
      prevInitialRef.current = initialJson;
    }
    setMsg(null);
    setErr(null);
  }, [initialJson, finalSubUserId, initialValues]);


  // ============ HELPERS ============
  const parseBlacklisted = useCallback((text) => {
    if (!text) return [];
    return text
      .split(/[,|\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
  }, []);


  // ============ HANDLERS ============
  const handleBlacklistedChange = useCallback(
    (e) => {
      setBlacklistedText(e.target.value);
      setBlacklisted(parseBlacklisted(e.target.value));
    },
    [parseBlacklisted]
  );


  const handleSubmit = useCallback(async () => {
    setErr(null);
    setMsg(null);
    if (!finalSubUserId) {
      setErr('No sub-user selected');
      return;
    }


    const normalizedMinProjects =
      minProjects === '' || minProjects === null || Number.isNaN(Number(minProjects))
        ? null
        : Number(minProjects);


    const payload = {
      client_filters: {
        ...(clientFilters || {}),
        payment_verified: paymentVerified || '',
        email_verified: emailVerified || '',
        deposit_made: depositMade || '',
        min_rating: minRating || '',
        min_projects: normalizedMinProjects,
        blacklisted_clients: Array.isArray(blacklisted)
          ? blacklisted.slice()
          : parseBlacklisted(blacklistedText),
      },
    };


    setSaving(true);
    try {
      await updateSubUser(finalSubUserId, payload);
      setMsg('✅ Saved successfully');
    } catch (e) {
      console.error('Failed to save client_filters', e);
      setErr(`❌ ${e?.message || 'Save failed'}`);
    } finally {
      setSaving(false);
    }
  }, [
    finalSubUserId,
    paymentVerified,
    emailVerified,
    depositMade,
    minRating,
    minProjects,
    blacklisted,
    blacklistedText,
    clientFilters,
    parseBlacklisted,
    updateSubUser,
  ]);


  const handleReset = useCallback(() => {
    setPaymentVerified(initialValues.payment_verified);
    setEmailVerified(initialValues.email_verified);
    setDepositMade(initialValues.deposit_made);
    setMinRating(initialValues.min_rating);
    setMinProjects(initialValues.min_projects);
    setBlacklisted(initialValues.blacklisted_clients || []);
    setBlacklistedText((initialValues.blacklisted_clients || []).join(', '));
    setMsg(null);
    setErr(null);
  }, [initialValues]);


  // ============ RENDER ============
  return (
    <div className="w-full p-6 bg-white rounded-lg shadow">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900">Client Filters</h2>
        <p className="text-sm text-gray-600 mt-2">
          Configure client requirements for auto-bidding eligibility.
        </p>
      </div>


      {/* Info Alert */}
      {/* <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
        <div className="flex items-start gap-3">
          <svg
            className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <div className="flex-1">
            <p className="text-sm font-medium text-yellow-800">
              Notice: You will see projects on the search page regardless of these filters.
            </p>
            <p className="text-sm text-yellow-700 mt-1">
              Projects that match these filters will be ignored when auto-bidding is enabled.
            </p>
          </div>
        </div>
      </div> */}


      {/* Status Messages */}
      {(msg || err || storeError) && (
        <div className="mb-6 flex items-center gap-2">
          {saving && <span className="text-blue-600 text-sm font-medium">⏳ Saving...</span>}
          {msg && !saving && <span className="text-green-600 text-sm font-medium">{msg}</span>}
          {err && !saving && <span className="text-red-600 text-sm font-medium">{err}</span>}
          {storeError && !err && !saving && (
            <span className="text-red-600 text-sm font-medium">{storeError}</span>
          )}
        </div>
      )}


      {/* Filter Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Payment Verified */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Payment verified
          </label>
          <select
            value={paymentVerified}
            onChange={(e) => setPaymentVerified(e.target.value)}
            className="w-full border-[1.5px] border-primary-blue rounded-lg px-4 py-3 text-sm bg-white focus:outline-none focus:border-2 focus:border-primary-blue transition-all"
          >
            {YES_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>


        {/* Email Verified */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Email verified</label>
          <select
            value={emailVerified}
            onChange={(e) => setEmailVerified(e.target.value)}
            className="w-full border-[1.5px] border-primary-blue rounded-lg px-4 py-3 text-sm bg-white focus:outline-none focus:border-2  focus:border-primary-blue transition-all"
          >
            {YES_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>


        {/* Deposit Made */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Deposit made</label>
          <select
            value={depositMade}
            onChange={(e) => setDepositMade(e.target.value)}
            className="w-full border-[1.5px] border-primary-blue rounded-lg px-4 py-3 text-sm bg-white focus:outline-none focus:border-2           focus:border-primary-blue transition-all"
          >
            {DEPOSIT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value} className='hover:bg-primary-blue'>
                {o.label}
              </option>
            ))}     
          </select>
        </div>


        {/* Minimum Rating */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Minimum rating</label>
          <select
            value={minRating}
            onChange={(e) => setMinRating(e.target.value)}
            className="w-full border-[1.5px] border-primary-blue rounded-lg px-4 py-3 text-sm bg-white focus:outline-none focus:border-2 focus:border-primary-blue transition-all"
          >
            {RATING_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>


        {/* Minimum Projects */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Minimum projects
          </label>
          <input
            type="number"
            min="0"
            value={minProjects === null ? '' : minProjects}
            onChange={(e) => setMinProjects(e.target.value === '' ? '' : Number(e.target.value))}
            className="w-full md:w-64 border-[1.5px] border-primary-blue rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-2 focus:border-primary-blue transition-all"
            placeholder="e.g. 5"
          />
          <p className="text-xs text-gray-500 mt-1">
            Filter clients by minimum number of projects posted
          </p>
        </div>
      </div>


      {/* Blacklisted Clients */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Blacklisted clients
        </label>
        <textarea
          value={blacklistedText}
          onChange={handleBlacklistedChange}
          placeholder="username1, username2, username3"
          className="w-full border-[1.5px] border-primary-blue rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-2 focus:border-primary-blue transition-all min-h-[100px] resize-y"
        />
        <p className="text-xs text-gray-500 mt-1">
          Enter usernames separated by comma or newline. These clients will be excluded from
          auto-bidding.
        </p>
      </div>


      {/* Blacklisted Preview */}
      {blacklisted.length > 0 && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-600 font-medium mb-2">
            Blacklisted Clients ({blacklisted.length}):
          </p>
          <div className="flex flex-wrap gap-2">
            {blacklisted.map((username, index) => (
              <span
                key={index}
                className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-xs font-medium"
              >
                {username}
              </span>
            ))}
          </div>
        </div>
      )}


      {/* Footer Buttons - Matching Other Filters */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={handleReset}
          disabled={saving}
          className="px-5 py-2 border-[1.5px] border-primary-blue text-gray-700 rounded-lg hover:bg-primary-blue hover:text-white disabled:opacity-60 transition-all font-medium"
        >
          Reset
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={saving || !finalSubUserId}
          className="px-6 py-2.5 bg-primary-blue text-white rounded-lg hover:bg-primary-blue/80 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all font-medium"
        >
          {saving ? ' Saving...' : ' Save Changes'}
        </button>
      </div>
    </div>
  );
};


ClientFilters.propTypes = {
  subUserId: PropTypes.string,
};


export default memo(ClientFilters);
